/**
 * CohortCaseViewer - Operating Point Visualization for Regulatory Cohorts
 *
 * Displays all conditions with their 4 operating point classifications,
 * allowing users to see how findings change across thresholds.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getApiBaseUrl } from "../config/api";
import {
  type OpLevel,
  OP_LEVELS_ASCENDING,
  OP_LABELS,
  OP_SHORT_LABELS,
  detectDeviceType,
} from "../config/operatingPoints";
import type { NativePrediction } from "../types/predictions";
import { getTopSlice } from "../utils/predictions";

// Component-specific descriptions for tooltips
const OP_DESCRIPTIONS: Record<OpLevel, string> = {
  high_sensitivity: "Most sensitive, fewer misses",
  sensitivity_biased: "Balanced toward sensitivity",
  balanced: "Equal sensitivity/specificity",
  high_specificity: "Most specific, fewer false positives",
};

// Derived array for iteration (ascending order for this component's UI)
const OPERATING_POINTS = OP_LEVELS_ASCENDING.map((key) => ({
  key,
  label: OP_LABELS[key].replace(" Confidence", ""), // Shorten for compact display
  short: OP_SHORT_LABELS[key],
  description: OP_DESCRIPTIONS[key],
}));

type OperatingPoint = OpLevel;

// Rung this viewer opens on, per device — the one the device itself claims, so the
// viewer doesn't offer a third answer alongside the worklist and the report.
// drafter ships default_operating_point "balanced" for all 182 validated findings;
// ct-suite claims high_specificity (its DEVICE_DETECTED_THRESHOLD); cortex is
// per-finding, so there is no single right rung — keep its historical default.
// The switcher still exposes every rung for exploration.
// TODO: true per-finding gating for drafter/cortex needs recommendedThreshold
// plumbed onto ConditionData; this aligns the default meanwhile.
const INITIAL_OP_BY_DEVICE: Record<ReturnType<typeof detectDeviceType>, OperatingPoint> = {
  drafter: "balanced",
  "ct-suite": "high_specificity",
  cortex: "sensitivity_biased",
};
const FALLBACK_INITIAL_OP: OperatingPoint = "sensitivity_biased";

interface ConditionData {
  condition: string;
  organ: string;
  finding: string;
  operatingPoints: Record<OperatingPoint, boolean | null>;
  topSlice?: string;
}

interface CohortCaseViewerProps {
  studyId?: string;
}

const CohortCaseViewer: React.FC<CohortCaseViewerProps> = ({ studyId: propStudyId }) => {
  const [searchParams] = useSearchParams();
  const studyId = propStudyId || searchParams.get("study") || "";
  const { token, userId } = useAuth();

  const [conditions, setConditions] = useState<ConditionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Set from the study's own device once predictions load (see INITIAL_OP_BY_DEVICE).
  // Keyed off device rather than hardcoded: "balanced" is right for drafter but
  // stricter than before for every other regulatory cohort, and wrong for ct-suite.
  const [selectedOp, setSelectedOp] = useState<OperatingPoint>(FALLBACK_INITIAL_OP);
  // Once the reader picks a rung, don't yank it out from under them on a refetch.
  const opPickedByUser = useRef(false);
  const [showFlippersOnly, setShowFlippersOnly] = useState(false);
  const [expandedOrgans, setExpandedOrgans] = useState<Set<string>>(new Set());

  // Fetch predictions
  useEffect(() => {
    if (!studyId || !token || !userId) return;

    const fetchPredictions = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${getApiBaseUrl()}/api/get_model_prediction`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id: userId, study_id: studyId }),
        });

        if (!response.ok) throw new Error(`API error: ${response.status}`);

        const data = await response.json();
        if (!data.base_model_prediction) {
          throw new Error(
            `get_model_prediction response missing base_model_prediction for study ${studyId}`,
          );
        }
        const preds = data.base_model_prediction as Record<string, NativePrediction>;

        const parsed: ConditionData[] = [];
        const organsWithPositives = new Set<string>();

        for (const [condition, pred] of Object.entries(preds)) {
          const parts = condition.split("/");
          const entry: ConditionData = {
            condition,
            organ: parts[0] || "unknown",
            finding: parts.slice(1).join("/"),
            operatingPoints: {
              high_sensitivity: pred.operating_points.high_sensitivity,
              sensitivity_biased: pred.operating_points.sensitivity_biased,
              balanced: pred.operating_points.balanced,
              high_specificity: pred.operating_points.high_specificity,
            },
          };
          const topSlice = getTopSlice(pred);
          if (topSlice) entry.topSlice = topSlice;
          parsed.push(entry);

          if (Object.values(entry.operatingPoints).some((v) => v === true)) {
            organsWithPositives.add(entry.organ);
          }
        }

        setConditions(parsed);
        setExpandedOrgans(organsWithPositives);

        // Open on the rung this study's device claims. device_id is the model
        // string (see aws_backend.get_model_prediction). An unrecognised model
        // throws out of detectDeviceType — keep the fallback rung and say so
        // rather than letting it kill the whole load.
        if (!opPickedByUser.current) {
          try {
            setSelectedOp(INITIAL_OP_BY_DEVICE[detectDeviceType(data.device_id)]);
          } catch (err) {
            console.warn(
              `CohortCaseViewer: unrecognised device_id ${String(data.device_id)} for study ` +
                `${studyId}; opening at ${FALLBACK_INITIAL_OP}`,
              err,
            );
          }
        }
      } catch (err) {
        console.error(`CohortCaseViewer: failed to load predictions for study ${studyId}`, err);
        setError(err instanceof Error ? err.message : "Failed to load predictions");
      } finally {
        setLoading(false);
      }
    };

    fetchPredictions();
  }, [studyId, token, userId]);

  // Filter conditions
  const filteredConditions = useMemo(() => {
    if (!showFlippersOnly) return conditions;

    // Show only conditions that flip between operating points
    return conditions.filter((c) => {
      const values = Object.values(c.operatingPoints).filter((v) => v !== null);
      return values.length > 1 && new Set(values).size > 1;
    });
  }, [conditions, showFlippersOnly]);

  // Group by organ
  const groupedByOrgan = useMemo(() => {
    const groups: Record<string, ConditionData[]> = {};
    filteredConditions.forEach((c) => {
      if (!groups[c.organ]) groups[c.organ] = [];
      groups[c.organ].push(c);
    });

    // Sort conditions within each organ
    Object.values(groups).forEach((arr) => {
      arr.sort((a, b) => a.finding.localeCompare(b.finding));
    });

    return groups;
  }, [filteredConditions]);

  // Count positives at selected operating point
  const positiveCounts = useMemo(() => {
    const counts: Record<OperatingPoint, number> = {
      high_sensitivity: 0,
      sensitivity_biased: 0,
      balanced: 0,
      high_specificity: 0,
    };
    conditions.forEach((c) => {
      OPERATING_POINTS.forEach((op) => {
        if (c.operatingPoints[op.key] === true) counts[op.key]++;
      });
    });
    return counts;
  }, [conditions]);

  const toggleOrgan = (organ: string) => {
    const next = new Set(expandedOrgans);
    if (next.has(organ)) next.delete(organ);
    else next.add(organ);
    setExpandedOrgans(next);
  };

  if (!studyId) {
    return (
      <div className="p-8 text-center text-fg-muted">
        <p>No study selected.</p>
        <p className="text-sm mt-2">Add ?study=study::job_id to the URL</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 text-center text-fg-muted">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        Loading predictions...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-warn">
        <p>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-bg-2 text-white">
      {/* Header */}
      <div className="p-4 border-b border-border bg-surface-2">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold">Operating Point Viewer</h1>
            <p className="text-xs text-fg-muted mt-1">
              {conditions.length} conditions · {studyId.split("::")[1]?.substring(0, 40)}...
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showFlippersOnly}
              onChange={(e) => setShowFlippersOnly(e.target.checked)}
              className="rounded"
            />
            Show flippers only (
            {
              conditions.filter((c) => {
                const values = Object.values(c.operatingPoints).filter((v) => v !== null);
                return values.length > 1 && new Set(values).size > 1;
              }).length
            }
            )
          </label>
        </div>

        {/* Operating Point Selector */}
        <div className="flex gap-1">
          {OPERATING_POINTS.map((op) => (
            <button
              key={op.key}
              onClick={() => {
                opPickedByUser.current = true;
                setSelectedOp(op.key);
              }}
              className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
                selectedOp === op.key
                  ? "bg-primary text-white"
                  : "bg-surface text-fg-muted hover:bg-surface-2"
              }`}
              title={op.description}
            >
              <div>{op.label}</div>
              <div className="text-xs opacity-70">{positiveCounts[op.key]} positive</div>
            </button>
          ))}
        </div>
      </div>

      {/* Conditions List */}
      <div className="flex-1 overflow-y-auto p-4">
        {Object.entries(groupedByOrgan)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([organ, organConditions]) => {
            const isExpanded = expandedOrgans.has(organ);
            const organPositives = organConditions.filter(
              (c) => c.operatingPoints[selectedOp] === true,
            ).length;

            return (
              <div key={organ} className="mb-3">
                {/* Organ Header */}
                <button
                  onClick={() => toggleOrgan(organ)}
                  className="w-full flex items-center justify-between py-2 px-3 bg-surface-2 rounded-t hover:bg-surface transition-colors"
                >
                  <span className="font-medium text-sm capitalize">{organ.replace(/_/g, " ")}</span>
                  <span className="text-xs text-fg-muted">
                    {organPositives > 0 && (
                      <span className="text-signal-text-tint mr-2">{organPositives} positive</span>
                    )}
                    {organConditions.length} conditions
                    <span className="ml-2">{isExpanded ? "▼" : "▶"}</span>
                  </span>
                </button>

                {/* Conditions */}
                {isExpanded && (
                  <div className="bg-bg-2 border border-border border-t-0 rounded-b">
                    {/* Header Row */}
                    <div className="grid grid-cols-[1fr_repeat(4,48px)] gap-1 px-3 py-1 bg-surface text-xs text-fg-muted">
                      <span>Condition</span>
                      {OPERATING_POINTS.map((op) => (
                        <span key={op.key} className="text-center">
                          {op.short}
                        </span>
                      ))}
                    </div>

                    {/* Condition Rows */}
                    {organConditions.map((c) => {
                      const isPositiveAtSelected = c.operatingPoints[selectedOp] === true;
                      const isFlipping =
                        new Set(Object.values(c.operatingPoints).filter((v) => v !== null)).size >
                        1;

                      return (
                        <div
                          key={c.condition}
                          className={`grid grid-cols-[1fr_repeat(4,48px)] gap-1 px-3 py-1.5 border-t border-border text-sm ${
                            isPositiveAtSelected ? "bg-signal-tint" : ""
                          } ${isFlipping ? "border-l-2 border-l-border-strong" : ""}`}
                        >
                          <span className="truncate text-fg-muted" title={c.finding}>
                            {c.finding}
                          </span>
                          {OPERATING_POINTS.map((op) => {
                            const val = c.operatingPoints[op.key];
                            const isSelected = op.key === selectedOp;
                            return (
                              <span
                                key={op.key}
                                className={`text-center ${isSelected ? "font-bold" : ""}`}
                              >
                                {val === true ? (
                                  <span className={`text-signal-text-tint ${isSelected ? "" : ""}`}>
                                    ✓
                                  </span>
                                ) : val === false ? (
                                  <span className="text-fg-muted">✗</span>
                                ) : (
                                  <span className="text-fg-muted">-</span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default CohortCaseViewer;
